
  import { defineComponent, PropType } from 'vue';
  import { Cell, ColumnDef, ColumnHelper, createColumnHelper, GroupColumnDef } from '@tanstack/vue-table';

  import TableAggregated from '@/common/components/redesigned/Table/TableAggregated.vue';
  import {
    TPaginationConfig,
    TTableAggregatedProgressBarOptions,
  } from '@/common/components/redesigned/Table/types/tableTypes';
  import text from '@/common/mixins/text';

  import { TABLE_AGGREGATED_PROGRESS_BAR_OPTIONS } from '../../../constants/tableAggregatedProgress';
  import {
    FTS_MONTHLY_ACTIONS_NAME,
    FTS_MONTHLY_GETTERS_NAME,
    FTS_MONTHLY_MUTATIONS_NAME,
  } from '../../../store/ftsMonthly/name';
  import { IndicatorTypes, TAggregatedTradeType } from '../../../types/common-aggregated';
  import { FTSTradeDirections } from '../../../types/fts-common';
  import { TFTSMonthlyFilterRequest, TFTSMonthlyTableData, TFTSMonthlyTableItem } from '../../../types/fts-monthly';
  import { cellClassGetter, getTableCountries, TCellLevelConfig } from '../../../utils/fts/table';
  import { costTypeTableTextMap, weightTableTextMap } from '../../../utils/table';

  type TColumnColspanKeys = keyof TFTSMonthlyTableItem;

  type FTSMonthlyLeafColumn = ColumnDef<TFTSMonthlyTableItem, string>;
  type FTSMonthlyGroupColumn = GroupColumnDef<TFTSMonthlyTableItem, unknown>;
  type FTSMonthlyColumnDef = FTSMonthlyLeafColumn | FTSMonthlyGroupColumn;

  const CELL_LEVEL_CLASS = 'fts-monthly-table__cell--level';

  const ADDITIONAL_CELL_LEVEL_CONFIGS: TCellLevelConfig<TFTSMonthlyTableItem>[] = [
    {
      columnId: 'name',
      getLevel: (row) => row.name.valueLevel,
    },
  ];

  export default defineComponent({
    name: 'FTSMonthlyTable',
    components: {
      TableAggregated,
    },
    mixins: [text],
    props: {
      filter: {
        type: Object as PropType<TFTSMonthlyFilterRequest>,
        required: true,
      },
    },
    data() {
      return {
        storePaths: {
          action: FTS_MONTHLY_ACTIONS_NAME.searchTableData,
          getter: FTS_MONTHLY_GETTERS_NAME.getTableData,
          mutation: FTS_MONTHLY_MUTATIONS_NAME.setTableData,
        },
      };
    },
    computed: {
      getProgressBarOptions(): TTableAggregatedProgressBarOptions {
        return TABLE_AGGREGATED_PROGRESS_BAR_OPTIONS;
      },
      getPaginationConfig(): TPaginationConfig {
        return {
          size: 0, // При значении 0 бэк возвращает все записи
        };
      },
    },
    methods: {
      getIsColumnVisible(columnKey: TColumnColspanKeys): boolean {
        switch (columnKey) {
          case 'measure':
            return this.filter.isAdditionalUnits || this.filter.isAdditionalUnitsWithQuantity;
          case 'codeOkato':
          case 'regionRf':
            return this.filter.senderOkatoIds.length > 0 || this.filter.recipientOkatoIds.length > 0;
          case 'customsProcedure':
            return this.filter.customsProcedureIds.length > 0;
          default:
            return false;
        }
      },
      generateColumns(data: TFTSMonthlyTableData) {
        if (!data) return [];

        const columnHelper = createColumnHelper<TFTSMonthlyTableItem>();

        return [
          columnHelper.accessor((row: TFTSMonthlyTableItem) => row.code.value, {
            header: 'Код ТН ВЭД',
            size: 140,
            meta: {
              getRowspan: (row: TFTSMonthlyTableItem) => row.name.rowspan,
              fixed: true,
            },
          }),
          columnHelper.accessor((row: TFTSMonthlyTableItem) => row.name.value, {
            id: 'name',
            header: 'Наименование товара/страны',
            size: 220,
            meta: {
              getRowspan: (row: TFTSMonthlyTableItem) => row.name.rowspan,
              fixed: true,
              textAlign: 'left',
            },
          }),
          ...this.getMeasureCellByFilter(columnHelper),
          ...this.getTableCountries(columnHelper).filter((item) => !!item),
          ...this.getCodeOkatoCellByFilter(columnHelper),
          ...this.getCustomsProcedureCellByFilter(columnHelper),
          ...this.getRegionRfCellByFilter(columnHelper),
          ...this.getTableGroup(data, columnHelper),
        ];
      },
      getTableCountries(columnHelper: ColumnHelper<TFTSMonthlyTableItem>) {
        return getTableCountries(columnHelper, this.filter);
      },
      getTableGroup(data: TFTSMonthlyTableData, columnHelper: ColumnHelper<TFTSMonthlyTableItem>) {
        switch (this.filter.indicatorTypeId) {
          case IndicatorTypes.PRICE_AND_WEIGHT:
            return this.getTableGroupByPriceAndWeight(data, columnHelper);
          case IndicatorTypes.PRICE:
            return this.getTableGroupByPrice(data, columnHelper);
          case IndicatorTypes.WEIGHT:
            return this.getTableGroupByWeight(data, columnHelper);
        }
      },
      getTableGroupByPriceAndWeight(data: TFTSMonthlyTableData, columnHelper: ColumnHelper<TFTSMonthlyTableItem>) {
        return [
          ...data.periods.map((period, index) => {
            return columnHelper.group({
              header: period,
              id: `${period}${index}`,
              columns: [
                ...this.getExportImportGroupsCells(
                  columnHelper,
                  index,
                  [
                    ...this.getExportQuantityCell(columnHelper, index),
                    this.getExportWeightCell(columnHelper, index),
                    this.getExportPriceCell(columnHelper, index),
                    ...this.getExportProportionCellByTotalShare(columnHelper, index),
                  ],
                  [
                    ...this.getImportQuantityCell(columnHelper, index),
                    this.getImportWeightCell(columnHelper, index),
                    this.getImportPriceCell(columnHelper, index),
                    ...this.getImportProportionCellByTotalShare(columnHelper, index),
                  ],
                ),
              ],
            });
          }),
        ];
      },
      getTableGroupByPrice(data: TFTSMonthlyTableData, columnHelper: ColumnHelper<TFTSMonthlyTableItem>) {
        return [
          ...data.periods.map((period, index) => {
            return columnHelper.group({
              header: period,
              id: `${period}${index}`,
              columns: [
                ...this.getExportImportGroupsCells(
                  columnHelper,
                  index,
                  [
                    ...this.getExportQuantityCell(columnHelper, index),
                    this.getExportPriceCell(columnHelper, index),
                    ...this.getExportProportionCellByTotalShare(columnHelper, index),
                  ],
                  [
                    ...this.getImportQuantityCell(columnHelper, index),
                    this.getImportPriceCell(columnHelper, index),
                    ...this.getImportProportionCellByTotalShare(columnHelper, index),
                  ],
                ),
              ],
            });
          }),
        ];
      },
      getTableGroupByWeight(data: TFTSMonthlyTableData, columnHelper: ColumnHelper<TFTSMonthlyTableItem>) {
        return [
          ...data.periods.map((period, index) => {
            return columnHelper.group({
              header: period,
              id: `${period}${index}`,
              columns: [
                ...this.getExportImportGroupsCells(
                  columnHelper,
                  index,
                  [
                    ...this.getExportQuantityCell(columnHelper, index),
                    this.getExportWeightCell(columnHelper, index),
                    ...this.getExportProportionCellByTotalShare(columnHelper, index),
                  ],
                  [
                    ...this.getImportQuantityCell(columnHelper, index),
                    this.getImportWeightCell(columnHelper, index),
                    ...this.getImportProportionCellByTotalShare(columnHelper, index),
                  ],
                ),
              ],
            });
          }),
        ];
      },
      getGroupCells(
        columnHelper: ColumnHelper<TFTSMonthlyTableItem>,
        index: number,
        columns: FTSMonthlyColumnDef[],
        header: string,
      ) {
        return columnHelper.group({
          header,
          columns,
        });
      },
      getImportGroupCells(
        columnHelper: ColumnHelper<TFTSMonthlyTableItem>,
        index: number,
        columns: FTSMonthlyColumnDef[],
      ) {
        return this.getGroupCells(columnHelper, index, columns, 'Импорт');
      },
      getExportGroupCells(
        columnHelper: ColumnHelper<TFTSMonthlyTableItem>,
        index: number,
        columns: FTSMonthlyColumnDef[],
      ) {
        return this.getGroupCells(columnHelper, index, columns, 'Экспорт');
      },
      getExportImportGroupsCells(
        columnHelper: ColumnHelper<TFTSMonthlyTableItem>,
        index: number,
        exportColumns: FTSMonthlyColumnDef[],
        importColumns: FTSMonthlyColumnDef[],
      ): FTSMonthlyColumnDef[] {
        switch (this.filter.tradeDirectionId) {
          case FTSTradeDirections.EXPORT: {
            return [this.getExportGroupCells(columnHelper, index, exportColumns)];
          }
          case FTSTradeDirections.IMPORT: {
            return [this.getImportGroupCells(columnHelper, index, importColumns)];
          }
          case FTSTradeDirections.EXPORT_IMPORT: {
            return [
              this.getExportGroupCells(columnHelper, index, exportColumns),
              this.getImportGroupCells(columnHelper, index, importColumns),
            ];
          }
        }
      },
      getPriceCell(columnHelper: ColumnHelper<TFTSMonthlyTableItem>, index: number, tradeType: TAggregatedTradeType) {
        const costTypeText = costTypeTableTextMap[this.filter.costInId];

        return columnHelper.accessor(
          (row: TFTSMonthlyTableItem) => this.formatNumber(row.indicators[index]?.[tradeType].price, 1),
          {
            header: `Стоимость, ${costTypeText}`,
            id: `${tradeType}-price-${index}`,
            size: 170,
          },
        );
      },
      getImportPriceCell(columnHelper: ColumnHelper<TFTSMonthlyTableItem>, index: number) {
        return this.getPriceCell(columnHelper, index, 'import');
      },
      getExportPriceCell(columnHelper: ColumnHelper<TFTSMonthlyTableItem>, index: number) {
        return this.getPriceCell(columnHelper, index, 'export');
      },
      getWeightCell(columnHelper: ColumnHelper<TFTSMonthlyTableItem>, index: number, tradeType: TAggregatedTradeType) {
        const weightText = weightTableTextMap[this.filter.weightId];

        return columnHelper.accessor(
          (row: TFTSMonthlyTableItem) => this.formatNumber(row.indicators[index]?.[tradeType].weight, 1),
          {
            header: `Вес, ${weightText}`,
            id: `${tradeType}-weight-${index}`,
            size: 170,
          },
        );
      },
      getImportWeightCell(columnHelper: ColumnHelper<TFTSMonthlyTableItem>, index: number) {
        return this.getWeightCell(columnHelper, index, 'import');
      },
      getExportWeightCell(columnHelper: ColumnHelper<TFTSMonthlyTableItem>, index: number) {
        return this.getWeightCell(columnHelper, index, 'export');
      },
      getProportionCell(
        columnHelper: ColumnHelper<TFTSMonthlyTableItem>,
        index: number,
        tradeType: TAggregatedTradeType,
      ) {
        return columnHelper.accessor(
          (row: TFTSMonthlyTableItem) => this.formatNumber(row.indicators[index]?.[tradeType].proportion, 1),
          {
            header: 'Доля, %',
            id: `${tradeType}-proportion-${index}`,
            size: 160,
          },
        );
      },
      getProportionCellByTotalShare(
        columnHelper: ColumnHelper<TFTSMonthlyTableItem>,
        index: number,
        tradeType: TAggregatedTradeType,
      ) {
        return [...(this.filter.isTotalShare ? [this.getProportionCell(columnHelper, index, tradeType)] : [])];
      },
      getImportProportionCellByTotalShare(columnHelper: ColumnHelper<TFTSMonthlyTableItem>, index: number) {
        return this.getProportionCellByTotalShare(columnHelper, index, 'import');
      },
      getExportProportionCellByTotalShare(columnHelper: ColumnHelper<TFTSMonthlyTableItem>, index: number) {
        return this.getProportionCellByTotalShare(columnHelper, index, 'export');
      },
      getQuantityCell(
        columnHelper: ColumnHelper<TFTSMonthlyTableItem>,
        index: number,
        tradeType: TAggregatedTradeType,
      ) {
        return columnHelper.accessor(
          (row: TFTSMonthlyTableItem) => this.formatNumber(row.indicators[index]?.[tradeType].quantity, 1),
          {
            header: 'Количество',
            id: `${tradeType}-quantity-${index}`,
            size: 160,
          },
        );
      },
      getQuantityCellByAdditionalUnitsWithQuantity(
        columnHelper: ColumnHelper<TFTSMonthlyTableItem>,
        index: number,
        tradeType: TAggregatedTradeType,
      ) {
        return [
          ...(this.filter.isAdditionalUnitsWithQuantity ? [this.getQuantityCell(columnHelper, index, tradeType)] : []),
        ];
      },
      getImportQuantityCell(columnHelper: ColumnHelper<TFTSMonthlyTableItem>, index: number) {
        return this.getQuantityCellByAdditionalUnitsWithQuantity(columnHelper, index, 'import');
      },
      getExportQuantityCell(columnHelper: ColumnHelper<TFTSMonthlyTableItem>, index: number) {
        return this.getQuantityCellByAdditionalUnitsWithQuantity(columnHelper, index, 'export');
      },
      getMeasureCell(columnHelper: ColumnHelper<TFTSMonthlyTableItem>) {
        return columnHelper.accessor((row: TFTSMonthlyTableItem) => row.measure, {
          header: 'Ед. изм.',
          id: 'measure',
          size: 140,
          meta: {
            fixed: true,
          },
        });
      },
      getMeasureCellByFilter(columnHelper: ColumnHelper<TFTSMonthlyTableItem>) {
        return [...(this.getIsColumnVisible('measure') ? [this.getMeasureCell(columnHelper)] : [])];
      },
      getCodeOkatoCell(columnHelper: ColumnHelper<TFTSMonthlyTableItem>) {
        return columnHelper.accessor((row: TFTSMonthlyTableItem) => row.codeOkato, {
          header: 'Код ОКАТО',
          id: 'codeOkato',
          size: 100,
          meta: {
            fixed: true,
          },
        });
      },
      getCodeOkatoCellByFilter(columnHelper: ColumnHelper<TFTSMonthlyTableItem>) {
        return [...(this.getIsColumnVisible('codeOkato') ? [this.getCodeOkatoCell(columnHelper)] : [])];
      },
      getRegionRfCell(columnHelper: ColumnHelper<TFTSMonthlyTableItem>) {
        return columnHelper.accessor((row: TFTSMonthlyTableItem) => row.regionRf, {
          header: 'Субъект РФ',
          id: 'regionRf',
          size: 180,
          meta: {
            textAlign: 'left',
            fixed: true,
          },
        });
      },
      getRegionRfCellByFilter(columnHelper: ColumnHelper<TFTSMonthlyTableItem>) {
        return [...(this.getIsColumnVisible('regionRf') ? [this.getRegionRfCell(columnHelper)] : [])];
      },
      getCustomsProcedureCell(columnHelper: ColumnHelper<TFTSMonthlyTableItem>) {
        return columnHelper.accessor((row: TFTSMonthlyTableItem) => row.customsProcedure, {
          header: 'Таможенная процедура',
          id: 'customs-procedure',
          size: 180,
          meta: {
            textAlign: 'left',
            fixed: true,
          },
        });
      },
      getCustomsProcedureCellByFilter(columnHelper: ColumnHelper<TFTSMonthlyTableItem>) {
        return [...(this.getIsColumnVisible('customsProcedure') ? [this.getCustomsProcedureCell(columnHelper)] : [])];
      },
      cellClassGetter(cell: Cell<TFTSMonthlyTableItem, string>) {
        let result = '';

        if (cell.row.original.isBold) {
          result = 'fts-monthly-table__cell--bold';
        }

        result = `${result} ${cellClassGetter(cell, CELL_LEVEL_CLASS, ADDITIONAL_CELL_LEVEL_CONFIGS)}`;

        return result;
      },
    },
  });
